/**
 * BiteSize — every lesson chopped into short pieces you can read in under a
 * minute. Filter by test, read one at a time (marks each read as you go) or as a
 * single feed. Ports renderBites. Bite HTML is our own trusted content.
 */
import { useEffect, useMemo, useState } from 'react';
import { tests, type TestKey } from '@/content/training';
import { useTrainingProgress } from '../TrainingProgressContext';
import { buildBites, type Bite } from '../lessonSections';
import { Card, Button, ProgressBar } from './TrainingUI';

type Scope = 'all' | TestKey;

function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: [T, string][];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted)' }}>
        {label}
      </div>
      <div className="pill-row" style={{ margin: 0 }}>
        {options.map(([v, lab]) => (
          <button key={v} onClick={() => onChange(v)} className={`pill${value === v ? ' on' : ''}`}>
            {lab}
          </button>
        ))}
      </div>
    </div>
  );
}

function BiteCard({ bite }: { bite: Bite }) {
  return (
    <Card>
      <div className="muted" style={{ fontSize: 12.5 }}>
        {tests[bite.test].name} · {bite.lessonTitle}
      </div>
      <div className="mt-1 text-sm" style={{ fontWeight: 600 }}>
        {bite.title}
      </div>
      <div className="lesson-body mt-3" dangerouslySetInnerHTML={{ __html: bite.html }} />
    </Card>
  );
}

export function BiteSize() {
  const progress = useTrainingProgress();
  const { markBiteRead } = progress;
  const all = useMemo(() => buildBites(), []);
  const [scope, setScope] = useState<Scope>('all');
  const [view, setView] = useState<'one' | 'feed'>('one');
  const [i, setI] = useState(0);

  const list = useMemo(
    () => (scope === 'all' ? all : all.filter((b) => b.test === scope)),
    [all, scope],
  );

  const changeScope = (v: Scope) => {
    setScope(v);
    setI(0);
  };

  const current = list[Math.min(i, list.length - 1)];

  // reading a bite (one-at-a-time) marks it read
  const currentId = current?.id;
  useEffect(() => {
    if (view === 'one' && currentId) markBiteRead(currentId);
  }, [view, currentId, markBiteRead]);

  const readCount = list.filter((b) => progress.bitesRead[b.id]).length;

  return (
    <div>
      <p className="mb-4 text-sm text-muted-foreground">
        Every lesson, chopped into {all.length} small pieces you can knock out in under a minute
        each. Take them one at a time or scroll the whole feed.
      </p>

      <Card className="mb-4 flex flex-wrap gap-6">
        <Segmented
          label="Which test"
          options={[
            ['all', 'All'],
            ['gk', 'General Knowledge'],
            ['ab', 'Air Brakes'],
            ['cv', 'Combination'],
          ]}
          value={scope}
          onChange={changeScope}
        />
        <Segmented
          label="View"
          options={[
            ['one', 'One at a time'],
            ['feed', 'All at once'],
          ]}
          value={view}
          onChange={setView}
        />
      </Card>

      <Card className="mb-4">
        <div className="muted" style={{ fontSize: 12.5 }}>
          {readCount} of {list.length} bites read in this selection
        </div>
        <ProgressBar pct={Math.round((100 * readCount) / Math.max(1, list.length))} className="mt-2" />
      </Card>

      {list.length === 0 ? (
        <Card>
          <p className="muted">No bites in this selection.</p>
        </Card>
      ) : view === 'feed' ? (
        <div className="space-y-4">
          {list.map((b) => (
            <BiteCard key={b.id} bite={b} />
          ))}
        </div>
      ) : (
        <div>
          {current && <BiteCard bite={current} />}
          <div className="mt-4 flex items-center gap-3">
            <Button
              variant="ghost"
              className="small"
              onClick={() => setI((v) => (v - 1 + list.length) % list.length)}
            >
              ← Prev
            </Button>
            <Button variant="primary" className="small" onClick={() => setI((v) => (v + 1) % list.length)}>
              Next bite →
            </Button>
            <span className="muted" style={{ fontSize: 12.5 }}>
              Bite {Math.min(i, list.length - 1) + 1} of {list.length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
